import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const fmt = (cur, v) => `${cur === "NGN" ? "₦" : "US$"}${Number(v || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;

function AllocateForm({ s, onDone }) {
  const [prog, setProg] = useState("");
  const [amt, setAmt] = useState("");
  const save = async () => {
    try { await api.post(`/admin/sponsorships/${s.id}/allocate`, { programme_name: prog, amount: Number(amt) }); toast.success("Allocation recorded"); setProg(""); setAmt(""); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex gap-1">
      <Input className="h-8 w-36 text-xs" placeholder="Programme" value={prog} onChange={(e) => setProg(e.target.value)} data-testid={`alloc-programme-${s.ref}`} />
      <Input className="h-8 w-24 text-xs" type="number" placeholder="Amount" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid={`alloc-amount-${s.ref}`} />
      <Button size="sm" className="h-8" disabled={!prog || !amt} onClick={save} data-testid={`alloc-save-${s.ref}`}>Allocate</Button>
    </div>
  );
}

export const AdminSponsorships = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/admin/sponsorships").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const s = d.summary;
  const cards = [["Funds received", fmt(s.currency, s.total_received)], ["Payment fees", fmt(s.currency, s.total_fees)], ["Refunded", fmt(s.currency, s.refunded)],
    ["Allocated", fmt(s.currency, s.allocated)], ["Unallocated balance", fmt(s.currency, s.unallocated)], ["Active monthly", s.active_monthly],
    ["Failed renewals", s.failed_payments], ["Abandoned checkouts", s.abandoned_checkouts]];
  return (
    <div className="mt-8 rounded-3xl border border-emerald-100 bg-white p-6" data-testid="admin-sponsorships-panel">
      <h2 className="font-display font-bold text-lg text-emerald-950">Family Sponsorships (donor pool)</h2>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(([k, v]) => <div key={k} className="rounded-xl bg-emerald-50 p-3"><div className="text-xs text-gray-500">{k}</div><div className="font-semibold text-emerald-950" data-testid={`sponsorship-stat-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div></div>)}
      </div>
      <div className="mt-4 overflow-auto max-h-96">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-400 text-xs uppercase"><th className="py-2">Ref</th><th>Donor</th><th>Pledge</th><th>Purpose</th><th>Received</th><th>Status</th><th>Allocate</th></tr></thead>
          <tbody>
            {d.sponsorships.length === 0 && <tr><td colSpan={7} className="py-4 text-gray-500">No confirmed sponsorships yet.</td></tr>}
            {d.sponsorships.map((x) => (
              <tr key={x.id} className="border-t border-gray-100 align-top" data-testid={`sponsorship-row-${x.ref}`}>
                <td className="py-2 font-mono text-xs">{x.ref}</td>
                <td className="text-xs">{x.donor_name}<span className="block text-gray-400">{x.donor_email}</span></td>
                <td className="text-xs">US${x.amount_usd}{x.frequency === "monthly" ? `/mo · ${x.duration_months || "ongoing"}${x.duration_months ? "m" : ""}` : " once"}</td>
                <td className="text-xs">{x.purpose_label}</td>
                <td className="text-xs">{fmt(x.currency, x.total_received)}<span className="block text-gray-400">{(x.allocations || []).length} allocation(s)</span></td>
                <td><Badge className={x.status === "active" || x.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{x.status}</Badge></td>
                <td>{x.total_received > 0 && <AllocateForm s={x} onDone={load} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
